import {useClpCourseQuery, useCoursesByIdQuery} from '@/generated/graphql';
import {adaptCourse} from '@/app/lib/course/course-adapter';
import {Course} from '@udemy-v2/browse-course';

/**
 * Fetches course data from GraphQL API and transforms it to our application's Course type
 * @param courseLandingUrl - The landing URL of the course to fetch
 * @param headers - optional HTTP headers to include in the request
 * @param locale - The locale identifier for language preference (e.g., 'en_US', 'es_ES')
 * @returns The transformed Course object
 * @throws Error if the course cannot be fetched or transformed
 */
export const fetchCourseData = async (
    courseLandingUrl: string,
    headers: Readonly<Headers>,
    locale?: string,
): Promise<Course> => {
    const headersObj = Object.fromEntries(headers.entries());

    // Add Accept-Language header if locale is provided
    if (locale) {
        // Convert locale format from 'en_US' to 'en-US' for HTTP header
        const acceptLanguage = locale.replace('_', '-');
        headersObj['Accept-Language'] = acceptLanguage;
    }
    try {
        if (process.env.DEBUG) {
            console.debug('Fetching course data for', courseLandingUrl);
        }
        // Fetch course data from GraphQL API using the generated query fetcher
        const variables = {
            landingURL: courseLandingUrl,
            includeVariationAggregation: true,
        };

        const response = await useClpCourseQuery.fetcher(variables, new Headers(headersObj))();

        // Check if course data exists
        if (!response?.courseByLandingURL) {
            throw new Error(`Unable to resolve course data with landing URL ${courseLandingUrl}`);
        }
        const course = response.courseByLandingURL;

        // Transform GraphQL course data to our application's Course type
        return adaptCourse(course);
    } catch (error) {
        console.warn('Error fetching course data:', error);
        throw error instanceof Error ? error : new Error('Failed to fetch course data');
    }
};

/**
 * Fetches course data from GraphQL API and transforms it to our application's Course type
 * @param id - The ID of the course to fetch
 * @param headers - optional HTTP headers to include in the request
 * @param locale - The locale identifier for language preference (e.g., 'en_US', 'es_ES')
 * @returns The transformed Course object
 * @throws Error if the course cannot be fetched or transformed
 */
export const fetchCourseDataByIds = async (
    ids: string[],
    headers?: Readonly<Headers>,
    locale?: string,
): Promise<Course[]> => {
    const headersObj = headers ? Object.fromEntries(headers.entries()) : {};

    // Add Accept-Language header if locale is provided
    if (locale) {
        // Convert locale format from 'en_US' to 'en-US' for HTTP header
        const acceptLanguage = locale.replace('_', '-');
        headersObj['Accept-Language'] = acceptLanguage;
    }
    try {
        if (process.env.DEBUG) {
            console.debug('Fetching course data by ids for', ids);
        }
        // Fetch course data from GraphQL API using the generated query fetcher
        const variables = {
            ids: ids,
            includeVariationAggregation: false,
        };

        const response = await useCoursesByIdQuery.fetcher(variables, new Headers(headersObj))();

        // Check if course data exists
        if (!response?.courses) {
            throw new Error(`Unable to resolve course data with ids ${ids}`);
        }
        const courses = response.courses;

        // Transform GraphQL course data to our application's Course type
        return courses.filter((course) => course !== null).map((course) => adaptCourse(course));
    } catch (error) {
        console.warn('Error fetching course data by id:', error);
        throw error instanceof Error ? error : new Error('Failed to fetch course data by id');
    }
};
